import dayjs, { Dayjs } from 'dayjs';
import { baseUrl, ApiError, GetAccessTokenSilently } from './api';
import { DateTime } from "luxon";

import { Patient } from './patients';

interface PdfResponse {
  ok: boolean;
  status: number;
  statusText: string;
  headers: Headers;
  blob: () => Promise<Blob>;
}

export interface UtilsMixin {
  fetch(path: string, init?: RequestInit): Promise<any>;
  getAccessTokenSilently?: GetAccessTokenSilently;
  fetchPdf(path: string, init?: RequestInit): Promise<any>;
  generateReport(id: number, name: string): Promise<PdfResponse>;
  generateTranscript(id: number, language: string, name: string): Promise<PdfResponse>;
  sendReportToPatient(id: number, language: string): Promise<any>;
  sendQuickMessage(patients: Patient[], title: string, message: string): Promise<any>;
  getChatwootToken(): Promise<any>;
}

export const UtilsMixin = {
  async fetch(path: string, init?: RequestInit): Promise<any> {
    throw new Error('Not implemented');
  },
  async fetchPdf(path: string, init?: RequestInit): Promise<any> {
    const token = this.getAccessTokenSilently ? await this.getAccessTokenSilently() : null;
    const authObject: any = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await fetch(baseUrl + path, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/pdf',
        ...authObject,
      },
      ...init,
    });

    if (res.ok) {
      try {
        return await res;
      } catch (e) {
        if (e instanceof SyntaxError) {
          return undefined;
        } else {
          throw e;
        }
      }
    } else {
      try {
        const err = await res.json();
        throw new ApiError(err.errors.map((err: { message: string }) => err.message).join('\n'));
      } catch (e) {
        if (e instanceof SyntaxError) {
          throw new ApiError(`Received ${res.status} ${res.statusText}`);
        } else {
          throw e;
        }
      }
    }
  },
  async generateReport(id: number, name: string): Promise<PdfResponse> {
    const timeZone = DateTime.local().zoneName;
    const pdfRes = await this.fetchPdf(
      `/sessions/${id}/pdf/report?timeZone=${timeZone}&name=${name}`
    );
    return pdfRes;
  },
  async generateTranscript(id: number, language: string, name: string): Promise<PdfResponse> {
    const timeZone = DateTime.local().zoneName;
    const pdfRes = await this.fetchPdf(
      `/sessions/${id}/pdf/transcript?locale=${language}&timeZone=${timeZone}&name=${name}`
    );
    return pdfRes;
  },
  async sendReportToPatient(id: number, language: string): Promise<any> {
    const timeZone = DateTime.local().zoneName;
    const res = await this.fetchPdf(
      `/sessions/${id}/send-report?locale=${language}&timeZone=${timeZone}`,
      { method: 'post' }
    );
    return res;
  },
  async sendQuickMessage(patients: Patient[], title: string, message: string): Promise<any> {
    const res = await this.fetch('/quick_message', {
      method: 'post',
      body: JSON.stringify({
        patients: [...patients],
        title,
        message,
      }),
    });
    return res;
  },
  async getChatwootToken(): Promise<any> {
    const res = await this.fetch('/chatwoot/token');
    return res;
  },
} as UtilsMixin;

export function titleCase(str: string) {
  const splitStr = str.toLowerCase().split(' ');
  for (let i = 0; i < splitStr.length; ++i) {
    // You do not need to check if i is larger than splitStr length, as your for does that for you
    // Assign it back to the array
    splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
  }
  // Directly return the joined string
  return splitStr.join(' ');
}
